import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Input, InputBase, Stack, Typography } from '@lyearn/ui';
import { EntityMediaFragment } from '@/features/messages/graphql/fragments/generated/MediaType';
import { baseInputStyles } from '@/modules/corporate/modules/database/views/FormView/components/DatabaseFormEditor/components/styles';

import { QuestionPlayerProps, UserSurveyQuestionPlayerResponse } from '../../types';
import UserSurveyRichTextInput from '../UserSurveyRichTextInput';

const EMPTY_ARRAY: never[] = [];
const COMMENT_EDITOR_CLASSES = { input: 'p-0' };

interface QuestionCommentProps {
  className?: string;
  useRichTextForComment: QuestionPlayerProps['useRichTextForComment'];
  post: UserSurveyQuestionPlayerResponse['post'];
  commentTitle?: string | null;
  disableComments?: boolean;
  onPostChange: (post: UserSurveyQuestionPlayerResponse['post']) => void;
  onCommentTitleChange?: (val: string) => void;
  requireComments?: boolean;
}

const QuestionComment = (props: React.PropsWithChildren<QuestionCommentProps>) => {
  const { t } = useTranslation();
  const {
    className,
    post,
    onPostChange,
    useRichTextForComment,
    commentTitle,
    disableComments,
    onCommentTitleChange,
    requireComments,
  } = props;
  const message = post?.message || '';
  const attachments = post?.attachments || EMPTY_ARRAY;

  const onCommentChange = useEvent((nextValue: string) => {
    onPostChange({ message: nextValue });
  });

  const onChangeText = useEvent((nextValue: string) => {
    onPostChange({ message: nextValue, attachments });
  });

  const onChangeMedias = useEvent((nextValue: any[]) => {
    onPostChange({ message, attachments: nextValue });
  });

  const commentPlaceholder = requireComments
    ? t('UserSurvey:LearnerDetails.CommentPlaceholderRequired')
    : t('UserSurvey:LearnerDetails.CommentPlaceholder');

  return (
    <Stack className={className} direction="col" gapY={8}>
      {onCommentTitleChange ? (
        <Typography className="ms-8" color="text-text-paragraph" variant="body-short-02">
          <InputBase
            className={cn('w-full', baseInputStyles)}
            classes={COMMENT_EDITOR_CLASSES}
            defaultValue={commentTitle}
            placeholder={t('UserSurvey:LearnerDetails.CommentTitlePlaceholder')}
            onBlur={(e) => onCommentTitleChange(e.target.value)}
          />
        </Typography>
      ) : commentTitle ? (
        <Typography className="ms-8" color="text-text-paragraph" variant="body-short-02">
          {commentTitle}
        </Typography>
      ) : null}
      {useRichTextForComment ? (
        <UserSurveyRichTextInput
          key={commentPlaceholder}
          allowAttachments={!disableComments}
          disabled={disableComments}
          medias={attachments as EntityMediaFragment[]}
          placeholder={commentPlaceholder}
          value={message}
          onChangeMedias={onChangeMedias}
          onChangeText={onChangeText}
        />
      ) : (
        <Input
          fullWidth
          disabled={disableComments}
          placeholder={commentPlaceholder}
          size="large"
          value={message}
          onChangeText={onCommentChange}
        />
      )}
    </Stack>
  );
};

export default QuestionComment;
